/**
 * @lodex-pro/web-portal/routes/MqttInstructionSheet — the producer's instruction sheet for its
 * MQTT gateways (Phase 13 / Task 13.13; CONTEXT "Specific Ideas", D-10a, D-10b, A8).
 *
 * Everything a producer (or its integrator) types into a gateway: the endpoint, port 8883, the
 * server CA, and per gateway its client id and its one topic, with the real values filled in.
 * Then the message format, led by `tankId`, and how inches and barrels are stored. Printable.
 *
 * No certificate material: each gateway's certificate and private key were handed over once,
 * when Paelode set it up (13.11).
 */

import React from 'react';
import type { MqttGatewayView } from '@lodex-pro/shared/validation/mqtt-gateways';
import type { DeviceReadingsConnectionResponse } from '@lodex-pro/shared/validation/operator-portal-device-readings';

/** CONTEXT D-10b, verbatim. */
export const PAYLOAD_EXAMPLE = `{
  "messageId": "gw7-000123",
  "ts": "2026-10-27T14:05:00Z",
  "readings": [
    { "tankId": "T-101", "level_in": 96.5, "ts": "2026-10-27T14:04:55Z" },
    { "tankId": "T-102", "volume_bbl": 257 },
    { "tag": "site/north/t3", "level_in": 41.0 }
  ]
}`;

export const SIZE_UNKNOWN_TEXT =
  "If you send inches and the tank has no size (BBL per inch) in Tank setup, we store the inches and show them as '8 ft 4 in, size unknown' until you set the size. We never guess barrels.";

const SECTION: React.CSSProperties = { marginTop: '16px' };
const MONO: React.CSSProperties = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  fontSize: '13px',
  wordBreak: 'break-all',
};
const CELL: React.CSSProperties = {
  textAlign: 'left',
  padding: '6px 8px',
  borderBottom: '1px solid var(--color-border-primary)',
  verticalAlign: 'top',
};

/** A gateway's topic, as 13.11 issues it: lodex/<env>/v1/<producerRef>/<gatewayRef>/readings. */
export function gatewayTopic(env: string, producerRef: string, gatewayRef: string): string {
  return `lodex/${env}/v1/${producerRef}/${gatewayRef}/readings`;
}

export function MqttInstructionSheet({
  connection,
  gateways,
}: {
  readonly connection: DeviceReadingsConnectionResponse;
  readonly gateways: readonly MqttGatewayView[];
}): React.JSX.Element {
  const active = gateways.filter((g) => g.status === 'ACTIVE');
  const env = connection.env;
  return (
    <section aria-label="Gateway instruction sheet" data-testid="mqtt-instruction-sheet">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h2 style={{ margin: 0, fontSize: '18px' }}>Gateway instruction sheet</h2>
        <button
          type="button"
          onClick={() => window.print()}
          style={{
            padding: '6px 14px',
            minHeight: '32px',
            cursor: 'pointer',
            background: 'transparent',
            color: 'var(--color-text-primary)',
            border: '1px solid var(--color-border-secondary)',
            borderRadius: '4px',
          }}
        >
          Print
        </button>
      </div>

      <div style={SECTION}>
        <h3 style={{ fontSize: '15px', marginBottom: '6px' }}>Connection</h3>
        <dl style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '4px 16px' }}>
          <dt>Endpoint</dt>
          <dd style={{ ...MONO, margin: 0 }}>
            {connection.endpoint ?? 'Ask Paelode for the endpoint.'}
          </dd>
          <dt>Port</dt>
          <dd style={{ margin: 0 }}>
            <span style={MONO}>8883</span> (MQTT over TLS)
          </dd>
          <dt>Server CA</dt>
          <dd style={{ margin: 0 }}>
            {connection.rootCa === null ? (
              'Amazon Root CA 1'
            ) : (
              <a href={connection.rootCa.url} target="_blank" rel="noreferrer">
                {connection.rootCa.name}
              </a>
            )}
            : your gateway trusts this CA to verify our endpoint.
          </dd>
        </dl>
      </div>

      <div style={SECTION}>
        <h3 style={{ fontSize: '15px', marginBottom: '6px' }}>Your gateways</h3>
        {active.length === 0 || env === null || connection.producerRef === null ? (
          <p>No active gateways yet. Paelode sets them up: ask your lodeX contact.</p>
        ) : (
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead>
              <tr>
                <th style={CELL}>Gateway</th>
                <th style={CELL}>Client ID</th>
                <th style={CELL}>Topic</th>
              </tr>
            </thead>
            <tbody>
              {active.map((g) => (
                <tr key={g.id}>
                  <td style={CELL}>{g.label}</td>
                  <td style={{ ...CELL, ...MONO }}>{g.thingName}</td>
                  <td style={{ ...CELL, ...MONO }}>
                    {gatewayTopic(env, connection.producerRef ?? g.producerRef, g.gatewayRef)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p>
          Use the client ID exactly as shown. Publish only to this topic. Any other topic is
          refused.
        </p>
        <p>
          One certificate per gateway: never share a certificate between two gateways (they&rsquo;ll
          keep disconnecting each other).
        </p>
      </div>

      <div style={SECTION}>
        <h3 style={{ fontSize: '15px', marginBottom: '6px' }}>Message format</h3>
        <pre
          style={{
            ...MONO,
            background: 'var(--color-bg-primary)',
            border: '1px solid var(--color-border-primary)',
            borderRadius: '4px',
            padding: '12px',
            whiteSpace: 'pre-wrap',
          }}
        >
          {PAYLOAD_EXAMPLE}
        </pre>
        <ul style={{ paddingLeft: '20px', lineHeight: 1.6 }}>
          <li>
            <code>tankId</code> is your producer tank ID, as in Tank setup (case and spaces
            don&rsquo;t matter).
          </li>
          <li>
            Send <code>tag</code> only if the device can&rsquo;t send the tank ID.
          </li>
          <li>
            Each reading has exactly one of <code>level_in</code> (the gauge level in inches) or{' '}
            <code>volume_bbl</code> (barrels).
          </li>
          <li>
            <code>ts</code> is optional per reading; it defaults to the message&rsquo;s{' '}
            <code>ts</code>.
          </li>
          <li>At most 50 readings and 5 KB per message.</li>
          <li>One reading per tank per minute is stored.</li>
          <li>Readings up to 30 days old are accepted.</li>
          <li>
            Re-sending the same <code>messageId</code> is safe.
          </li>
        </ul>
      </div>

      <div style={SECTION}>
        <h3 style={{ fontSize: '15px', marginBottom: '6px' }}>Inches and barrels</h3>
        <p>{SIZE_UNKNOWN_TEXT}</p>
      </div>
    </section>
  );
}
